import { ZButton } from '@/shared/components/button';
import { Input, Select, SelectItem } from '@nextui-org/react';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';

interface QuestionnaireHeaderProps {
  statusFilter?: string;
  onStatusFilterChange?: (status: string) => void;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  onAutoFill?: () => void;
  onRespondOffline?: () => void;
  isAutoFillModalOpen: boolean;
  setIsAutoFillModalOpen: (open: boolean) => void;
  isRespondOfflineModalOpen: boolean;
  setIsRespondOfflineModalOpen: (open: boolean) => void;
}

const QuestionnaireHeader: React.FC<QuestionnaireHeaderProps> = ({
  statusFilter = 'all',
  onStatusFilterChange,
  searchQuery = '',
  onSearchChange,
  onAutoFill,
  onRespondOffline,
  isAutoFillModalOpen,
  setIsAutoFillModalOpen,
  isRespondOfflineModalOpen,
  setIsRespondOfflineModalOpen
}) => {
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);

  // Create debounced function using lodash
  const debouncedSearchChange = useRef(
    debounce((value: string) => {
      onSearchChange?.(value);
    }, 300)
  ).current;

  // Update local search when prop changes (for external updates)
  useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  // Handle search input change
  const handleSearchChange = useCallback((value: string) => {
    setLocalSearchQuery(value);
    debouncedSearchChange(value);
  }, [debouncedSearchChange]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearchChange.cancel();
    };
  }, [debouncedSearchChange]);
  const statusOptions = [
    { value: 'all', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'completed', label: 'Completed' },
    { value: 'mandatory', label: 'Mandatory' }
  ];

  return (
    <div 
      className="sticky top-0 z-20 flex flex-row justify-between items-center bg-white h-[60px] border-b border-[#E4E4E7]"
      style={{ padding: '12px 16px', boxSizing: 'border-box', marginLeft: '-24px', marginRight: '-24px', marginTop: '-16px', paddingLeft: '40px', paddingRight: '40px', width: 'calc(100% + 48px)' }}
    >
      {/* Left Section - Controls */}
      <div className="flex flex-row items-center flex-1" style={{ padding: '0px', gap: '8px', height: '36px' }}>
        {/* Status Dropdown */}
        <div>
          <Select
            placeholder="All"
            selectedKeys={statusFilter ? [statusFilter] : ['all']}
            onSelectionChange={(keys) => {
              const selectedArray = Array.from(keys);
              const selected = selectedArray[0] as string;
              onStatusFilterChange?.(selected);
            }}
            variant="flat"
            style={{ width: '160px' }}
          >
            {statusOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </Select>
        </div>
        {/* Search Input */}
        <div className="flex flex-row items-start bg-[#F4F4F5] rounded-md  w-[400px]">
          <Input
            placeholder="Search using keywords"
            value={localSearchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            variant="flat"
          />
        </div>
      </div>
      {/* Right Section - Action Buttons */}
      <div className="flex flex-row justify-end items-center flex-shrink-0" style={{ padding: '0px', gap: '8px', height: '24px' }}>
        {/* Auto-fill Button */}
        {/* TODO: once backend is done enable it and integrate apis */}
        {/* <ZButton
          variant="bordered"
          size="sm"
          onClick={onAutoFill}
          startContent={<Sparkles size={12} className="text-[#006FEE]" />}
          className="bg-[#FAFAFA] border border-[#006FEE] text-[#006FEE] text-xs font-medium rounded-md"
          style={{ padding: '4px 8px 4px 6px', gap: '6px', width: '79px', height: '24px', boxSizing: 'border-box' }}
        >
          <span className="text-xs font-medium text-[#006FEE]" style={{ fontFamily: 'Inter', fontSize: '12px', lineHeight: '16px', width: '47px', height: '16px' }}>
            Auto-fill
          </span>
        </ZButton> */}
        {/* Respond Offline Button */}
        <ZButton
          variant="bordered"
          size="sm"
          onClick={onRespondOffline}
          className="bg-[#FAFAFA] border border-[#006FEE] text-[#006FEE] text-xs font-medium rounded-md"
          style={{ padding: '4px 8px', gap: '6px', width: '108px', height: '24px', boxSizing: 'border-box' }}
        >
          <span className="text-xs font-medium text-[#006FEE]" style={{ fontFamily: 'Inter', fontSize: '12px', lineHeight: '16px', width: '92px', height: '16px' }}>
            Respond Offline
          </span>
        </ZButton>
      </div>
    </div>
  );
};

export default QuestionnaireHeader;
